<template>
  <nav
    v-for="([key, group], index) in Object.entries(navMenuSetUp)"
    :key="key"
    class="tw-bg-primary-grey-900 tw-relative"
  >
    <div
      v-if="sideNavSectionLabelByGroup(key, tier)"
      class="tw-relative tw-flex tw-items-center tw-my-1 tw-px-4 tw-py-1"
      :class="{
        'tw-invisible tw-h-0': isSidebarCollapsed,
      }"
    >
      <div class="tw-w-12 tw-border-t tw-border-primary-grey-700"></div>
      <span class="tw-px-2 tw-text-primary-grey-300 tw-uppercase">{{
        $t(sideNavSectionLabelByGroup(key, tier))
      }}</span>
      <div class="tw-flex-grow tw-border-t tw-border-primary-grey-700"></div>
    </div>
    <div
      v-if="isSidebarCollapsed || !sideNavSectionLabelByGroup(key, tier)"
      class="tw-my-2 tw-px-4 tw-py-2"
    >
      <hr class="tw-border-primary-grey-700" />
    </div>
    <ul>
      <li
        v-for="firstLevelItem in group"
        :key="firstLevelItem.id"
        class="tw-group tw-relative"
        v-tippy="firstLevelItem.tooltip"
        @click="onNavigationItemClicked(firstLevelItem)"
      >
        <a
          v-if="firstLevelItem.isExternal && firstLevelItem.externalLink"
          target="_blank"
          :href="firstLevelItem.externalLink"
          :class="{
            'tw-pointer-events-none tw-text-primary-grey-700':
              firstLevelItem.disabled,
          }"
          class="tw-flex tw-py-4 tw-px-4 hover:tw-bg-primary-grey-700 tw-transition-colors"
          :data-test-id="firstLevelItem.dataTestId"
          custom
        >
          <div class="tw-grid tw-grid-cols-12 tw-gap-0 tw-w-full">
            <div class="tw-col-span-2">
              <component :is="firstLevelItem.icon" />
            </div>
            <div
              class="tw-col-span-10 tw-flex tw-justify-between tw-items-center"
              :class="{
                'tw-invisible tw-h-0': isSidebarCollapsed,
              }"
            >
              <span>{{ firstLevelItem.label }}</span>
              <cl-pill
                v-if="firstLevelItem.permanentPill"
                :variant="firstLevelItem.permanentPill.variant"
                >{{ firstLevelItem.permanentPill.text }}</cl-pill
              >
              <cl-pill
                v-if="firstLevelItem.pill"
                :variant="firstLevelItem.pill.variant"
                >{{ firstLevelItem.pill.text }}</cl-pill
              >
              <cl-icon-tabler-external-link />
            </div>
          </div>
        </a>
        <router-link
          v-else
          v-slot="{ href, navigate, isActive, isExactActive }"
          custom
          :to="firstLevelItem.id"
        >
          <a
            :href="href"
            :class="{
              'tw-bg-primary-grey-700 tw-font-normal tw-shadow-[inset_0.1875rem_0] tw-shadow-primary-grey-600':
                !isExactActive && isActive,
              'tw-bg-primary-grey-700 tw-font-bold tw-shadow-[inset_0.1875rem_0] tw-shadow-white':
                isExactActive && isActive,
              'tw-pointer-events-none tw-text-primary-grey-700':
                firstLevelItem.disabled,
            }"
            class="tw-flex tw-py-4 tw-px-4 hover:tw-bg-primary-grey-700 tw-transition-colors"
            :data-test-id="firstLevelItem.dataTestId"
            custom
            @click="navigate"
          >
            <div class="tw-grid tw-grid-cols-12 tw-gap-0 tw-w-full">
              <div class="tw-col-span-2">
                <component :is="firstLevelItem.icon" />
              </div>
              <div
                class="tw-col-span-10 tw-flex tw-justify-between tw-items-center"
                :class="{
                  'tw-invisible tw-h-0': isSidebarCollapsed,
                }"
              >
                <span>{{ firstLevelItem.label }}</span>
                <cl-pill
                  v-if="firstLevelItem.permanentPill"
                  :variant="firstLevelItem.permanentPill.variant"
                  >{{ firstLevelItem.permanentPill.text }}</cl-pill
                >
                <cl-pill
                  v-if="firstLevelItem.pill"
                  :variant="firstLevelItem.pill.variant"
                  >{{ firstLevelItem.pill.text }}</cl-pill
                >
                <cl-icon-tabler-external-link
                  v-if="firstLevelItem.isExternal"
                />
              </div>
            </div>
          </a>
          <div
            v-if="
              firstLevelItem.children?.length &&
              shouldCollapseSidebar(isSidebarCollapsed, isActive)
            "
            class="tw-bg-primary-grey-700 tw-shadow-[inset_0.1875rem_0] tw-shadow-primary-grey-600"
            :class="
              isSidebarCollapsed
                ? 'group-hover:tw-block tw-left-[56px] tw-top-0 tw-absolute tw-hidden tw-w-[240px]'
                : 'tw-block'
            "
          >
            <ul class="tw-group">
              <li
                v-for="secondLevelItem in firstLevelItem.children"
                :key="secondLevelItem.id"
                class="tw-group"
              >
                <router-link
                  v-if="
                    shouldShowSecondLevelItems(
                      isSidebarCollapsed,
                      secondLevelItem.disabled ?? false,
                    )
                  "
                  v-slot="{
                    href: href2,
                    navigate: navigate2,
                    isActive: isActive2,
                    isExactActive: isExactActive2,
                  }"
                  custom
                  :to="secondLevelItem.id"
                >
                  <a
                    :href="href2"
                    :class="{
                      'tw-bg-primary-grey-600 tw-font-normal tw-shadow-[inset_0.1875rem_0] tw-shadow-primary-grey-600':
                        !isExactActive2 && isActive2,
                      'tw-bg-primary-grey-600 tw-font-bold tw-shadow-[inset_0.1875rem_0] tw-shadow-white':
                        isExactActive2 && isActive2,
                      'tw-pointer-events-none tw-text-primary-grey-700':
                        secondLevelItem.disabled,
                    }"
                    class="tw-flex tw-relative tw-py-4 tw-px-4 hover:tw-bg-primary-grey-600 tw-transition-colors"
                    :data-test-id="secondLevelItem.dataTestId"
                    custom
                    @click="navigate2"
                  >
                    <div class="tw-grid tw-grid-cols-12 tw-gap-0 tw-w-full">
                      <div
                        class="tw-col-start-3 tw-col-span-9 tw-flex tw-justify-between tw-items-center"
                      >
                        <span>{{ secondLevelItem.label }}</span>
                        <cl-pill
                          v-if="secondLevelItem.pill && !isActive2"
                          :variant="secondLevelItem.pill.variant"
                          >{{ secondLevelItem.pill.text }}</cl-pill
                        >
                        <cl-pill
                          v-if="secondLevelItem.permanentPill"
                          :variant="secondLevelItem.permanentPill.variant"
                          >{{ secondLevelItem.permanentPill.text }}</cl-pill
                        >
                      </div>
                      <div class="tw-col-span-1">
                        <cl-icon-tabler-chevron-down
                          v-if="
                            secondLevelItem.children?.length
                              ? secondLevelItem.children?.length
                              : false
                          "
                          :class="{ 'tw-rotate-180': isActive2 }"
                          class="tw-transition-transform"
                        />
                      </div>
                    </div>
                  </a>
                  <div
                    v-if="secondLevelItem.children?.length ? isActive2 : false"
                    class="tw-bg-primary-grey-600"
                    :class="'tw-shadow-[inset_0.1875rem_0] tw-shadow-primary-grey-600'"
                  >
                    <ul>
                      <li
                        v-for="thirdLevelItem in secondLevelItem.children"
                        :key="thirdLevelItem.id"
                      >
                        <router-link
                          v-slot="{
                            href: href3,
                            navigate: navigate3,
                            isActive: isActive3,
                          }"
                          :to="thirdLevelItem.id"
                          custom
                        >
                          <a
                            :href="href3"
                            :class="{
                              'tw-bg-primary-grey-600   tw-font-normal tw-shadow-[inset_0.1875rem_0] tw-shadow-primary-grey-600':
                                !isActive3,
                              'tw-bg-[#4E5670] tw-font-bold tw-shadow-[inset_0.1875rem_0] tw-shadow-white':
                                isActive3,
                              'tw-pointer-events-none tw-text-primary-grey-700':
                                thirdLevelItem.disabled,
                            }"
                            class="tw-flex tw-relative tw-py-4 tw-px-4 hover:tw-bg-[#4E5670] tw-transition-colors"
                            :data-test-id="thirdLevelItem.dataTestId"
                            @click="navigate3"
                          >
                            <div
                              class="tw-grid tw-grid-cols-12 tw-gap-0 tw-w-full"
                            >
                              <div
                                class="tw-col-start-4 tw-col-span-9 tw-flex tw-justify-between tw-items-center"
                              >
                                <span>{{ thirdLevelItem.label }}</span>
                                <cl-pill
                                  v-if="thirdLevelItem.pill"
                                  :variant="thirdLevelItem.pill.variant"
                                  >{{ thirdLevelItem.pill.text }}</cl-pill
                                >
                              </div>
                            </div>
                          </a>
                        </router-link>
                      </li>
                    </ul>
                  </div>
                </router-link>
              </li>
            </ul>
          </div>
        </router-link>
      </li>
    </ul>
    <div
      v-if="index + 1 === Object.entries(navMenuSetUp).length"
      class="tw-my-2 tw-px-4 tw-py-2"
    >
      <hr class="tw-border-primary-grey-700" />
    </div>
  </nav>
</template>
<script setup lang="ts">
import { toRefs } from "vue";
import { TSideNavProps } from "./types";
import { sideNavSectionLabelByGroup } from "@/types/side-nav.ts";
import { SIDENAV_GROUP_BY, TIER } from "@/constants";
import { trackEvent } from "@/utils/track-event";

const props = defineProps<TSideNavProps>();
const { navMenuSetUp, isSidebarCollapsed, tier } = toRefs(props);

const shouldCollapseSidebar = (
  isSidebarCollapsed: boolean,
  isActive: boolean,
) => {
  if (isActive && !isSidebarCollapsed) {
    return true;
  } else if (!isActive && !isSidebarCollapsed) {
    return false;
  } else if (isActive && isSidebarCollapsed) {
    return true;
  } else if (!isActive && isSidebarCollapsed) {
    return true;
  }
};

const onNavigationItemClicked = (item: {
  groupBy: string;
  disabled?: boolean;
  label: string;
}) => {
  if (tier.value === TIER.msps && item.groupBy === SIDENAV_GROUP_BY.BETA) {
    const actionName = item.disabled
      ? "Disabled solution button clicked"
      : "Solution button clicked";
    trackEvent({
      category: "MSP Side Navigation",
      action: actionName,
      name: item.label,
    });
  }
};

const shouldShowSecondLevelItems = (
  isSidebarCollapsed: boolean,
  disabled: boolean,
) => {
  return !(isSidebarCollapsed && disabled);
};
</script>
